import { cn } from '@/lib/utils';

interface Column<T> {
  key: keyof T | string;
  header: string;
  render?: (value: any, row: T) => React.ReactNode;
  className?: string;
}

interface ReportTableProps<T> {
  data: T[];
  columns: Column<T>[];
  emptyMessage?: string;
  maxHeight?: string;
  onRowClick?: (row: T) => void;
}

export default function ReportTable<T extends Record<string, any>>({
  data,
  columns,
  emptyMessage = 'No data available',
  maxHeight = 'max-h-96',
  onRowClick,
}: ReportTableProps<T>) {
  const getValue = (row: T, key: string) => {
    const keys = key.split('.');
    let value: any = row;
    for (const k of keys) {
      value = value?.[k];
    }
    return value;
  };

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className={cn('overflow-auto', maxHeight)}>
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-muted/50 backdrop-blur-sm">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key as string}
                className={cn(
                  'text-left px-4 py-3 font-medium text-muted-foreground border-b',
                  col.className
                )}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y">
          {data.map((row, index) => (
            <tr 
              key={index} 
              className={cn(
                'hover:bg-muted/50 transition-colors',
                onRowClick && 'cursor-pointer'
              )}
              onClick={() => onRowClick?.(row)}
            >
              {columns.map((col) => {
                const value = getValue(row, col.key as string);
                return (
                  <td
                    key={col.key as string}
                    className={cn('px-4 py-3', col.className)}
                  >
                    {col.render ? col.render(value, row) : value ?? '-'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
